import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Shield, Trophy, Dumbbell, Mail, FileText, ExternalLink, ChevronDown, ChevronUp, Building2, MapPin, Phone, ShieldCheck, AlertTriangle, LinkIcon, Download } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AdminLayout from '../../components/AdminLayout';
import api from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScoreBadge = ({ score }) => {
  if (score === null || score === undefined) return <span className="text-xs text-[#9CA3AF]">N/A</span>;
  const color = score >= 80 ? 'bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]' :
                score >= 60 ? 'bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]' :
                'bg-[#EEF0FF] text-[#7B6FE8] border-[#C4BFFF]';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold border ${color}`}>
      {score}
    </span>
  );
};

const ProtectionDot = ({ level }) => {
  const c = level?.includes('Well') ? 'bg-[#065F46]' : level?.includes('Partial') ? 'bg-[#92400E]' : 'bg-[#DC2626]';
  return <span className={`inline-block w-2 h-2 rounded-full ${c} shrink-0`} />;
};

const AdminRiskAssessments = () => {
  const [assessments, setAssessments] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [trackFilter, setTrackFilter] = useState('all');
  const [emailFilter, setEmailFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { loadAssessments(); }, []);

  useEffect(() => {
    let list = assessments;
    if (trackFilter !== 'all') list = list.filter(a => a.assessment_type === trackFilter);
    if (emailFilter === 'captured') list = list.filter(a => a.has_email);
    else if (emailFilter === 'anonymous') list = list.filter(a => !a.has_email);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(a =>
        (a.org_name || '').toLowerCase().includes(q) ||
        (a.email || '').toLowerCase().includes(q) ||
        (a.org_phone || '').toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [assessments, searchTerm, trackFilter, emailFilter]);

  const loadAssessments = async () => {
    try {
      const res = await api.get('/admin/risk-assessments');
      setAssessments(res.data);
    } catch (err) {
      console.error('Failed to load risk assessments', err);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    total: assessments.length,
    withEmail: assessments.filter(a => a.has_email).length,
    withCoi: assessments.filter(a => a.has_coi).length,
    sports: assessments.filter(a => a.assessment_type === 'sports').length,
    fitness: assessments.filter(a => a.assessment_type === 'fitness').length,
    avgScore: assessments.length > 0
      ? Math.round(assessments.reduce((s, a) => s + (a.risk_score || 0), 0) / assessments.length)
      : 0,
  };

  const statusColor = (s) => {
    if (s === 'submitted') return 'bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]';
    if (s === 'quoted') return 'bg-[#EEF0FF] text-[#6B5CE7] border-[#C4BFFF]';
    if (['accepted','bound','closed_won'].includes(s)) return 'bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]';
    if (s === 'closed_lost') return 'bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]';
    return 'bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B6FE8]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-black text-[#1A1A1A] uppercase tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Risk Assessments
          </h1>
          <p className="text-[#6B7280] mt-1 text-sm">Lead pipeline from the Risk Assessment tool</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-white border border-[#E5E7EB] shadow-none relative overflow-hidden" data-testid="stat-total-assessments">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-[#6B7280]">Total</p>
              <p className="text-2xl font-black text-[#1A1A1A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5E7EB] shadow-none" data-testid="stat-with-email">
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-[#6B7280]">Email Captured</p>
              <p className="text-2xl font-black text-[#7B6FE8]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{stats.withEmail}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5E7EB] shadow-none" data-testid="stat-with-coi">
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-[#6B7280]">With COI</p>
              <p className="text-2xl font-black text-[#065F46]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{stats.withCoi}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5E7EB] shadow-none" data-testid="stat-sports">
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-[#6B7280]">Sports</p>
              <p className="text-2xl font-black text-[#6B5CE7]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{stats.sports}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5E7EB] shadow-none" data-testid="stat-fitness">
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-[#6B7280]">Fitness</p>
              <p className="text-2xl font-black text-[#4ECDC4]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{stats.fitness}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5E7EB] shadow-none" data-testid="stat-avg-score">
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-[#6B7280]">Avg Score</p>
              <p className="text-2xl font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: stats.avgScore >= 80 ? '#065F46' : stats.avgScore >= 60 ? '#92400E' : '#7B6FE8' }}>{stats.avgScore || '—'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 relative min-w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#9CA3AF]" />
            <Input
              data-testid="search-assessments-input"
              placeholder="Search by organization, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11 border-[#E5E7EB] focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
            />
          </div>
          <Select value={trackFilter} onValueChange={setTrackFilter}>
            <SelectTrigger data-testid="track-filter-select" className="w-44 h-11 border-[#E5E7EB]">
              <SelectValue placeholder="All Tracks" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tracks</SelectItem>
              <SelectItem value="sports">
                <span className="flex items-center gap-2"><Trophy className="h-4 w-4 text-[#7B6FE8]" /> Sports</span>
              </SelectItem>
              <SelectItem value="fitness">
                <span className="flex items-center gap-2"><Dumbbell className="h-4 w-4 text-[#4ECDC4]" /> Fitness</span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={emailFilter} onValueChange={setEmailFilter}>
            <SelectTrigger data-testid="email-filter-select" className="w-44 h-11 border-[#E5E7EB]">
              <SelectValue placeholder="All Leads" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leads</SelectItem>
              <SelectItem value="captured">
                <span className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#065F46]" /> Email Captured</span>
              </SelectItem>
              <SelectItem value="anonymous">
                <span className="flex items-center gap-2"><Shield className="h-4 w-4 text-[#9CA3AF]" /> Anonymous</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Assessments Table */}
        <Card className="bg-white border border-[#E5E7EB] shadow-none">
          <CardHeader className="border-b border-[#E5E7EB] bg-[#F7F8FA]">
            <CardTitle className="text-base font-semibold text-[#1A1A1A]">{filtered.length} Assessment{filtered.length !== 1 ? 's' : ''}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="h-10 w-10 text-[#D1D5DB] mx-auto mb-3" />
                <p className="text-[#6B7280]">No risk assessments found</p>
                <p className="text-xs text-[#9CA3AF] mt-1">Assessments will appear here when users complete the Risk Assessment tool</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E5E7EB]">
                {filtered.map((a) => {
                  const isExpanded = expandedId === a.id;
                  return (
                    <div key={a.id} data-testid={`assessment-row-${a.id}`}>
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : a.id)}
                        className="w-full text-left p-4 hover:bg-[#F3F4F6] transition-colors"
                        data-testid={`assessment-toggle-${a.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                              <h4 className="font-semibold text-[#1A1A1A] text-base">{a.org_name || 'Anonymous Assessment'}</h4>
                              {a.assessment_type === 'fitness' ? (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0]">
                                  <Dumbbell className="h-3 w-3" /> Fitness
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#EEF0FF] text-[#6B5CE7] border border-[#C4BFFF]">
                                  <Trophy className="h-3 w-3" /> Sports
                                </span>
                              )}
                              {a.has_email && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0]">
                                  <Mail className="h-3 w-3" /> Lead
                                </span>
                              )}
                              {a.has_coi && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#F0F9FF] text-[#0369A1] border border-[#BAE6FD]">
                                  <FileText className="h-3 w-3" /> COI
                                </span>
                              )}
                              {a.linked_applications?.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#EEF0FF] text-[#6B5CE7] border border-[#C4BFFF]">
                                  <LinkIcon className="h-3 w-3" /> App
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-6 text-sm text-[#6B7280]">
                              {a.email && <span>{a.email}</span>}
                              {a.org_phone && <span>{a.org_phone}</span>}
                              {a.org_address && <span className="truncate max-w-[200px]">{a.org_address}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 shrink-0">
                            <ScoreBadge score={a.risk_score} />
                            <div className="text-right text-sm text-[#6B7280]">
                              <div>{a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</div>
                              <div className="text-xs text-[#9CA3AF] mt-0.5">
                                {a.created_at ? new Date(a.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                              </div>
                            </div>
                            {isExpanded ? <ChevronUp className="h-4 w-4 text-[#9CA3AF]" /> : <ChevronDown className="h-4 w-4 text-[#9CA3AF]" />}
                          </div>
                        </div>
                      </button>

                      {/* Expanded Detail */}
                      {isExpanded && (
                        <div className="px-4 pb-5 bg-[#F9FAFB] border-t border-[#E5E7EB]" data-testid={`assessment-detail-${a.id}`}>
                          {/* Scores Row */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4">
                            <div>
                              <p className="text-xs text-[#6B7280] mb-1">Risk Score</p>
                              <ScoreBadge score={a.risk_score} />
                            </div>
                            <div>
                              <p className="text-xs text-[#6B7280] mb-1">Safety Score</p>
                              <p className="text-sm font-semibold text-[#1A1A1A]">
                                {a.safety_score?.earned ?? '—'} / {a.safety_score?.max ?? '—'}
                                {a.safety_score?.percentage !== undefined && <span className="text-[#6B7280] font-normal"> ({a.safety_score.percentage}%)</span>}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-[#6B7280] mb-1">Insurance Score</p>
                              <p className="text-sm font-semibold text-[#1A1A1A]">{a.insurance_score ?? '—'} / 100</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#6B7280] mb-1">Overall Posture</p>
                              <p className="text-sm font-semibold text-[#1A1A1A]">{a.overall_posture || '—'}</p>
                            </div>
                          </div>

                          {/* Organization Details */}
                          {(a.org_name || a.org_dba || a.org_address || a.org_phone || a.org_email) && (
                            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 mb-4" data-testid={`org-details-${a.id}`}>
                              <div className="flex items-center gap-2 mb-3">
                                <Building2 className="h-4 w-4 text-[#7B6FE8]" />
                                <p className="text-sm font-semibold text-[#1A1A1A]">Organization Details</p>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                {a.org_name && (
                                  <div><span className="text-[#6B7280]">Name:</span> <span className="text-[#1A1A1A] font-medium">{a.org_name}</span></div>
                                )}
                                {a.org_dba && (
                                  <div><span className="text-[#6B7280]">DBA:</span> <span className="text-[#1A1A1A]">{a.org_dba}</span></div>
                                )}
                                {a.org_address && (
                                  <div className="flex items-start gap-1">
                                    <MapPin className="h-3.5 w-3.5 text-[#6B7280] mt-0.5 shrink-0" />
                                    <span className="text-[#1A1A1A]">{a.org_address}</span>
                                  </div>
                                )}
                                {a.org_phone && (
                                  <div className="flex items-center gap-1">
                                    <Phone className="h-3.5 w-3.5 text-[#6B7280] shrink-0" />
                                    <span className="text-[#1A1A1A]">{a.org_phone}</span>
                                  </div>
                                )}
                                {a.org_email && (
                                  <div className="flex items-center gap-1">
                                    <Mail className="h-3.5 w-3.5 text-[#6B7280] shrink-0" />
                                    <span className="text-[#1A1A1A]">{a.org_email}</span>
                                  </div>
                                )}
                                {a.org_description && (
                                  <div><span className="text-[#6B7280]">Type:</span> <span className="text-[#1A1A1A]">{a.org_description}</span></div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* COI Files */}
                          {a.coi_file_ids && a.coi_file_ids.length > 0 && (
                            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 mb-4" data-testid={`coi-files-${a.id}`}>
                              <div className="flex items-center gap-2 mb-3">
                                <FileText className="h-4 w-4 text-[#0369A1]" />
                                <p className="text-sm font-semibold text-[#1A1A1A]">Uploaded COI Documents</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {a.coi_file_ids.map((fileId, i) => (
                                  <a
                                    key={fileId}
                                    href={`${API_URL}/api/risk-assessment/file/${fileId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`coi-file-link-${fileId}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#0369A1] bg-[#F0F9FF] border border-[#BAE6FD] rounded-lg hover:bg-[#E0F2FE] transition-colors"
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                    COI {a.coi_file_ids.length > 1 ? `#${i + 1}` : 'Document'}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* COI Coverages */}
                          {a.coverages && a.coverages.length > 0 && (
                            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 mb-4" data-testid={`coi-coverages-${a.id}`}>
                              <div className="flex items-center gap-2 mb-3">
                                <ShieldCheck className="h-4 w-4 text-[#7B6FE8]" />
                                <p className="text-sm font-semibold text-[#1A1A1A]">Insurance Coverages from COI</p>
                              </div>
                              <div className="space-y-2">
                                {a.coverages.map((c, i) => (
                                  <div key={i} className="flex items-center justify-between gap-4 py-1.5 border-b border-[#F3F4F6] last:border-0 text-sm">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <ProtectionDot level={c.protection_level} />
                                      <span className="font-medium text-[#1A1A1A]">{c.type}</span>
                                    </div>
                                    <div className="flex items-center gap-4 text-[#6B7280] text-xs shrink-0">
                                      <span className={
                                        c.protection_level?.includes('Well') ? 'text-[#065F46] font-semibold' :
                                        c.protection_level?.includes('Partial') ? 'text-[#92400E] font-semibold' :
                                        'text-[#DC2626] font-semibold'
                                      }>{c.protection_level}</span>
                                      {c.per_occurrence_limit && c.per_occurrence_limit !== 'Not shown on certificate' && (
                                        <span>{c.per_occurrence_limit}</span>
                                      )}
                                      {c.carrier && <span>{c.carrier}</span>}
                                      {c.expiration && <span>Exp: {c.expiration}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Linked Applications */}
                          {a.linked_applications && a.linked_applications.length > 0 && (
                            <div className="bg-white border border-[#C4BFFF] rounded-lg p-4 mb-4" data-testid={`linked-apps-${a.id}`}>
                              <div className="flex items-center gap-2 mb-3">
                                <LinkIcon className="h-4 w-4 text-[#7B6FE8]" />
                                <p className="text-sm font-semibold text-[#1A1A1A]">Linked Applications</p>
                              </div>
                              <div className="space-y-2">
                                {a.linked_applications.map((app) => (
                                  <Link
                                    key={app.id}
                                    to={`/admin/applications/${app.id}`}
                                    data-testid={`linked-app-${app.id}`}
                                    className="flex items-center justify-between p-2.5 bg-[#EEF0FF] rounded-lg hover:bg-[#E0DEFF] transition-colors"
                                  >
                                    <div className="flex items-center gap-2">
                                      <FileText className="h-4 w-4 text-[#7B6FE8]" />
                                      <span className="text-sm font-medium text-[#1A1A1A]">{app.org_name || 'Application'}</span>
                                      <span className="text-xs text-[#6B7280]">#{app.id.substring(0, 8)}</span>
                                    </div>
                                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${statusColor(app.status)}`}>
                                      {app.status}
                                    </span>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Action Links */}
                          <div className="flex items-center gap-3">
                            <a
                              href={`${API_URL}/api/report/${a.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`view-report-link-${a.id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#7B6FE8] bg-[#EEF0FF] border border-[#C4BFFF] rounded-lg hover:bg-[#E0DEFF] transition-colors"
                            >
                              <ExternalLink className="h-3.5 w-3.5" /> View Full Report
                            </a>
                            {a.has_email && (
                              <a
                                href={`mailto:${a.email}`}
                                data-testid={`email-link-${a.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#065F46] bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg hover:bg-[#D1FAE5] transition-colors"
                              >
                                <Mail className="h-3.5 w-3.5" /> {a.email}
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminRiskAssessments;
